import { useEffect, useState } from "react";
import { Loader2, X, GraduationCap, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { updateTeacherProfile } from "../lib/auth";
import { CANONICAL_SUBJECTS, profileSubjectsAsKeys, keysToProfileLabels } from "../lib/subjects";

const ALL_GRADES = [
  "Grade 4",
  "Grade 5",
  "Grade 6",
  "Grade 7",
  "Grade 8",
];

// Phase 116.A — canonical 11-subject list. "The Arts" is split into
// Dance / Drama / Music / Visual Arts; "Health and Physical
// Education" is split into Health / Physical Education. Source of
// truth is `lib/subjects.js`.

const Checkbox = ({ checked, onToggle, label, testid }) => (
  <button
    type="button"
    onClick={onToggle}
    data-testid={testid}
    aria-pressed={checked}
    className={`group flex items-center gap-3 rounded-lg border px-4 py-3 text-left transition-all ${
      checked
        ? "border-[#1c2d3d] bg-[#1c2d3d] text-white shadow-sm"
        : "border-[#4a6984]/20 bg-white text-[#1c2d3d] hover:border-[#1c2d3d]/40 hover:bg-[#f4f5f7]"
    }`}
  >
    <span
      className={`h-4 w-4 shrink-0 rounded border-2 grid place-items-center transition-colors ${
        checked
          ? "border-white bg-white"
          : "border-[#4a6984]/40 bg-white group-hover:border-[#1c2d3d]/60"
      }`}
    >
      {checked && (
        <svg viewBox="0 0 12 12" className="h-3 w-3" aria-hidden="true">
          <path
            d="M2 6.2L4.8 9 10 3"
            fill="none"
            stroke="#1c2d3d"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </span>
    <span className="font-semibold text-sm">{label}</span>
  </button>
);

export const TeacherProfileModal = ({
  open,
  initialGrades = [],
  initialSubjects = [],
  initialConsentAt = null,
  isFirstTime = false,
  onClose,
  onSaved,
}) => {
  const [grades, setGrades] = useState(initialGrades);
  // Phase 116.A — subject state is the Set of canonical keys (not
  // labels). On open we normalise the legacy `subjects_taught` array
  // — which may carry old bundled labels like "The Arts" or "Health
  // and Physical Education" — into the canonical key set so the
  // right boxes pre-tick.
  const [subjectKeys, setSubjectKeys] = useState(() => profileSubjectsAsKeys(initialSubjects));
  const [saving, setSaving] = useState(false);
  // Privacy + Terms consent. Required on the very first profile save
  // (PIPEDA: consent before collection). For returning teachers who
  // already consented in a previous session, we pre-check the box and
  // never re-prompt.
  const [consented, setConsented] = useState(!!initialConsentAt);

  useEffect(() => {
    if (open) {
      setGrades(initialGrades);
      setSubjectKeys(profileSubjectsAsKeys(initialSubjects));
      setConsented(!!initialConsentAt);
    }
  }, [open, initialGrades, initialSubjects, initialConsentAt]);

  if (!open) return null;

  const toggle = (list, setter) => (val) =>
    setter(list.includes(val) ? list.filter((x) => x !== val) : [...list, val]);

  const toggleSubjectKey = (key) => {
    setSubjectKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (grades.length === 0 || subjectKeys.size === 0) {
      toast.error("Pick at least one grade and one subject so we can scope your tracker.");
      return;
    }
    if (!consented) {
      toast.error("Please review and accept the Privacy Policy and Terms of Service to continue.");
      return;
    }
    setSaving(true);
    try {
      const res = await updateTeacherProfile({
        grades_taught: grades,
        subjects_taught: keysToProfileLabels(subjectKeys),
        privacy_consent: consented,
      });
      toast.success("Classroom profile saved", {
        description: "Your Tracker now scopes expectations to your grades + subjects.",
      });
      onSaved?.(res);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Save failed";
      toast.error("Couldn't save your profile", { description: String(detail) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="teacher-profile-modal"
      className="fixed inset-0 z-50 grid place-items-center bg-[#1c2d3d]/55 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="ae-card relative w-full max-w-2xl max-h-[92vh] overflow-y-auto p-6 md:p-8 shadow-2xl">
        {!isFirstTime && (
          <button
            type="button"
            onClick={onClose}
            data-testid="teacher-profile-close"
            className="absolute top-3 right-3 h-9 w-9 rounded-full grid place-items-center text-[#4a6984] hover:bg-[#f4f5f7]"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        <div className="mb-6">
          <p className="font-bold uppercase text-[10px] tracking-[0.25em] text-[#4a6984]/70">
            {isFirstTime ? "Welcome to Atomic Edu" : "Classroom Profile"}
          </p>
          <h2 className="mt-2 font-extrabold text-2xl sm:text-3xl text-[#4a6984] tracking-tight">
            {isFirstTime ? "Tell us a bit about your classroom" : "Update your classroom"}
          </h2>
          <p className="mt-2 text-sm text-[#4a6984]/80 leading-relaxed">
            We&apos;ll use this to scope the <span className="font-semibold">Tracker</span> to
            the grades and subjects you actually teach — so the expectations
            checklist shows real coverage, not noise.
          </p>
        </div>

        <section className="mb-6">
          <header className="flex items-center gap-2 mb-3">
            <GraduationCap className="h-4 w-4 text-[#1c2d3d]" />
            <h3 className="font-extrabold text-sm text-[#1c2d3d] tracking-tight uppercase tracking-[0.1em]">
              Grades I teach
            </h3>
            <span className="text-xs text-[#4a6984]/60 font-medium">
              (pick one or more)
            </span>
          </header>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {ALL_GRADES.map((g) => (
              <Checkbox
                key={g}
                checked={grades.includes(g)}
                onToggle={() => toggle(grades, setGrades)(g)}
                label={g}
                testid={`profile-grade-${g.replace(/\s+/g, "-")}`}
              />
            ))}
          </div>
        </section>

        <section className="mb-8">
          <header className="flex items-center gap-2 mb-3">
            <BookOpen className="h-4 w-4 text-[#1c2d3d]" />
            <h3 className="font-extrabold text-sm text-[#1c2d3d] tracking-tight uppercase tracking-[0.1em]">
              Subjects I teach
            </h3>
            <span className="text-xs text-[#4a6984]/60 font-medium">
              (pick one or more)
            </span>
          </header>
          <p className="text-xs text-[#4a6984]/70 mb-3 leading-relaxed">
            Subjects you uncheck stay <strong>hidden</strong>, not deleted —
            re-check them anytime and your existing units, assessments, and
            coverage reappear.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {CANONICAL_SUBJECTS.map((s) => (
              <Checkbox
                key={s.key}
                checked={subjectKeys.has(s.key)}
                onToggle={() => toggleSubjectKey(s.key)}
                label={s.label}
                testid={`profile-subject-${s.key}`}
              />
            ))}
          </div>
        </section>

        <section
          data-testid="profile-consent-section"
          className="mt-2 mb-4 p-4 rounded-lg border border-[#4a6984]/15 bg-[#f4f5f7]/60"
        >
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={consented}
              onChange={(e) => setConsented(e.target.checked)}
              disabled={!!initialConsentAt}
              data-testid="profile-consent-checkbox"
              className="mt-1 h-4 w-4 accent-[#4a6984] shrink-0"
            />
            <span className="text-[13px] text-[#4a6984]/90 font-medium leading-relaxed">
              I have read and accept the{" "}
              <a
                href="/privacy"
                target="_blank"
                rel="noreferrer"
                data-testid="profile-consent-privacy-link"
                className="font-bold text-[#1c2d3d] underline decoration-[#96ded1] decoration-2 underline-offset-2 hover:decoration-[#1c2d3d]"
              >
                Privacy Policy
              </a>{" "}
              and the{" "}
              <a
                href="/terms"
                target="_blank"
                rel="noreferrer"
                data-testid="profile-consent-terms-link"
                className="font-bold text-[#1c2d3d] underline decoration-[#96ded1] decoration-2 underline-offset-2 hover:decoration-[#1c2d3d]"
              >
                Terms of Service
              </a>
              . I understand Atomic Edu sees only my email, name, and profile
              picture from Google by default. Connecting "Save to Google
              Drive" is optional, and only grants Atomic Edu permission to
              create files it uploads itself — never to read any other file
              in my Drive.
              {initialConsentAt && (
                <span className="block mt-1 text-[11px] text-[#4a6984]/65">
                  Consent recorded {new Date(initialConsentAt).toLocaleDateString()}.
                </span>
              )}
            </span>
          </label>
        </section>

        <div className="flex flex-wrap items-center justify-end gap-3">
          {!isFirstTime && (
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              data-testid="teacher-profile-cancel"
              className="px-4 py-2 rounded-md text-sm font-semibold text-[#4a6984] hover:bg-[#f4f5f7]"
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || grades.length === 0 || subjectKeys.size === 0 || !consented}
            data-testid="teacher-profile-save"
            className="ae-cta inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-bold disabled:opacity-60"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              <>{isFirstTime ? "Start using Atomic Edu" : "Save profile"}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeacherProfileModal;
